import {
  Plus,
  PlusBlue,
  PlusIcon,
  TargetIcon,
  WelcomeIcon
} from '../../../icons/SvgIcons';
import {
  GuideStep,
  GuideStepperItem
} from '../../../types/OnboardingGuide.types';

const AI_SYSTEM_LIFECYCLE_TITLE = 'Explore AI System life cycle';
const AI_SYSTEM_LIFECYCLE_DESCRIPTION = (
  <p className="font-medium">
    From the first spark of creation to continuous post-market oversight, here
    is exactly how you'll navigate the compliance life cycle within the
    platform.
  </p>
);
const AI_SYSTEM_LIFECYCLE_STEPS = [
  {
    key: 'LIFECYCLE_PHASE_1',
    title: 'From Idea to System',
    description:
      'Create your AI system by adding basic details and selecting your role.',
    visual: '/tutorial-guide/from-idea-to-system.png'
  },
  {
    key: 'LIFECYCLE_PHASE_2',
    title: 'Understand Your Risk',
    description:
      "Answer a short questionnaire to determine your system's risk level and get clear compliance requirements.",
    visual: '/tutorial-guide/understand-your-risk.mp4'
  },
  {
    key: 'LIFECYCLE_PHASE_3',
    title: 'The Compliance Path',
    description:
      'High-risk systems follow a 5-step process - from connecting your repo to completing conformity and CE marking.',
    visual: '/tutorial-guide/the-compliance-path.mp4'
  },
  {
    key: 'LIFECYCLE_PHASE_4',
    title: 'Smart Documentation',
    description:
      'Store, manage, and auto-generate compliance documents in one place.',
    visual: '/tutorial-guide/smart-documentation.mp4'
  },
  {
    key: 'LIFECYCLE_PHASE_5',
    title: 'Final Approval',
    description:
      'Complete your assessment, sign the declaration, and finalize your CE marking.',
    visual: '/tutorial-guide/final-approval.png'
  },
  {
    key: 'LIFECYCLE_PHASE_6',
    title: 'Stay in Control',
    description:
      'Track system activity, monitor trends, and export logs for audits.',
    visual: '/tutorial-guide/stay-in-control.png'
  }
] as const;

const getStepperItems = (currentStepKey: string): GuideStepperItem[] => {
  const currentStepIndex = AI_SYSTEM_LIFECYCLE_STEPS.findIndex(
    (step) => step.key === currentStepKey
  );

  return AI_SYSTEM_LIFECYCLE_STEPS.map((step, index) => ({
    id: index + 1,
    key: step.key,
    title: step.title,
    description: step.description,
    visual: step.visual,
    status:
      index < currentStepIndex
        ? 'COMPLETED'
        : index === currentStepIndex
          ? 'IN_PROGRESS'
          : 'UPCOMING'
  }));
};

export const TUTORIAL_GUIDE_STEPS: GuideStep[] = [
  {
    key: 'WELCOME',
    title: "Welcome - let's get you oriented.",
    icon: <WelcomeIcon />,
    description: (
      <>
        <p className="font-medium">
          Welcome to CompliAI, your streamlined path to EU AI Act compliance.
          This guide helps you navigate the platform's core features to govern
          your AI systems with confidence.
        </p>
        <p>
          You can skip this guide at any time or return to it later from the
          Help section.
        </p>
      </>
    ),
    nextButtonLabel: "Let's Start",
    nextButtonClassName: 'text-xs',
    backButtonLabel: 'Skip Tour'
  },
  {
    key: 'DASHBOARD_OVERVIEW',
    title: 'Quick Overview',
    description:
      'This is your main Dashboard where you can quickly see an overview of your AI systems, compliance status, key metrics, and recent activity.',
    modalClassName:
      'absolute left-28 2xl:left-[269px] top-[90px] 2xl:top-16 w-[472px]',
    showArrow: true,
    nextButtonLabel: 'Next',
    nextButtonClassName: 'w-[92px] leading-5'
  },
  {
    key: 'AI_SYSTEMS_OVERVIEW',
    title: 'Start and Manage Your Compliance Journey',
    description:
      'This is where you create and manage all your AI systems, track their compliance status, and access detailed system information.',
    modalClassName:
      'absolute left-28 2xl:left-[269px] top-[155px] 2xl:top-[109px] w-[472px]',
    showArrow: true,
    nextButtonLabel: 'Next',
    nextButtonClassName: 'w-[92px] leading-5',
    backButtonLabel: 'Previous'
  },
  {
    key: 'USER_MANAGEMENT_OVERVIEW',
    title: "Structure Your Team's Access",
    description:
      'This is where you manage users, assign roles, and control access across the platform.',
    modalClassName:
      'absolute left-28 2xl:left-[269px] top-[224px] 2xl:top-[153px] w-[472px]',
    showArrow: true,
    nextButtonLabel: 'Next',
    nextButtonClassName: 'w-[92px] leading-5',
    backButtonLabel: 'Previous'
  },
  {
    key: 'SETTINGS_OVERVIEW',
    title: 'Settings',
    description:
      'This is where you update your account, manage security, and set your preferences.',
    modalClassName:
      'absolute left-28 2xl:left-[269px] top-[291px] 2xl:top-[196px] w-[472px]',
    showArrow: true,
    nextButtonLabel: 'Next',
    nextButtonClassName: 'w-[92px] leading-5',
    backButtonLabel: 'Previous'
  },
  {
    key: 'LIFECYCLE_INTRO',
    icon: <TargetIcon />,
    title: 'The Compliance Engine: A Deep Dive into AI Systems',
    description: (
      <>
        <p>
          Now that you've got the lay of the land, it's time to look under the
          hood. We've moved past the "where" and "what" and now we're diving
          into the how.
        </p>
        <p>
          We'll walk through the actual life cycle of a high-risk AI system.
        </p>
      </>
    ),
    nextButtonLabel: 'See How',
    nextButtonClassName: 'text-xs w-[85px]',
    backButtonLabel: 'Previous'
  },
  {
    key: 'LIFECYCLE_PHASE_1',
    title: AI_SYSTEM_LIFECYCLE_TITLE,
    description: AI_SYSTEM_LIFECYCLE_DESCRIPTION,
    stepperItems: getStepperItems('LIFECYCLE_PHASE_1'),
    nextButtonLabel: 'Next',
    nextButtonClassName: 'text-xs w-[92px]',
    backButtonLabel: 'Previous'
  },
  {
    key: 'LIFECYCLE_PHASE_2',
    title: AI_SYSTEM_LIFECYCLE_TITLE,
    description: AI_SYSTEM_LIFECYCLE_DESCRIPTION,
    stepperItems: getStepperItems('LIFECYCLE_PHASE_2'),
    nextButtonLabel: 'Next',
    nextButtonClassName: 'text-xs w-[92px]',
    backButtonLabel: 'Previous'
  },
  {
    key: 'LIFECYCLE_PHASE_3',
    title: AI_SYSTEM_LIFECYCLE_TITLE,
    description: AI_SYSTEM_LIFECYCLE_DESCRIPTION,
    stepperItems: getStepperItems('LIFECYCLE_PHASE_3'),
    nextButtonLabel: 'Next',
    nextButtonClassName: 'text-xs w-[92px]',
    backButtonLabel: 'Previous'
  },
  {
    key: 'LIFECYCLE_PHASE_4',
    title: AI_SYSTEM_LIFECYCLE_TITLE,
    description: AI_SYSTEM_LIFECYCLE_DESCRIPTION,
    stepperItems: getStepperItems('LIFECYCLE_PHASE_4'),
    nextButtonLabel: 'Next',
    nextButtonClassName: 'text-xs w-[92px]',
    backButtonLabel: 'Previous'
  },
  {
    key: 'LIFECYCLE_PHASE_5',
    title: AI_SYSTEM_LIFECYCLE_TITLE,
    description: AI_SYSTEM_LIFECYCLE_DESCRIPTION,
    stepperItems: getStepperItems('LIFECYCLE_PHASE_5'),
    nextButtonLabel: 'Next',
    nextButtonClassName: 'text-xs w-[92px]',
    backButtonLabel: 'Previous'
  },
  {
    key: 'LIFECYCLE_PHASE_6',
    title: AI_SYSTEM_LIFECYCLE_TITLE,
    description: AI_SYSTEM_LIFECYCLE_DESCRIPTION,
    stepperItems: getStepperItems('LIFECYCLE_PHASE_6'),
    nextButtonLabel: 'Add AI System',
    nextButtonClassName: 'text-xs w-[141px]',
    nextButtonIcon: PlusIcon(),
    backButtonLabel: 'Previous'
  }
];
